import * as React from "react";
import { connect, ConnectedProps } from "react-redux";
import { RootState } from "../../../redux";
import { LanguageContext } from "../../../../../utilities/LocalizationModule";
import { ActionButton, DefaultButton, FontIcon, FontSizes, FontWeights, ILabelStyles, Label, Persona, PersonaSize, Stack, TooltipHost, Text, IIconProps } from "@fluentui/react";
import ExpanderWithSubtitle from "../ExpanderWithSubtitle";
import { expanderConstants } from "../../../redux/models/constants/expanders";
import { isMobile, mobileListItemStyle } from "../../../styles/mobileStyles";
import { getApprovals } from "../../../redux/modules/approvals";
import { IApproval } from "./ApprovalsModels";
import ApprovalsPanel from "./ApprovalsPanel";
import { ApprovalType, DefaultAction, defaultApprovalActions, IApprovalAction } from "./IApprovalAction";
import authModule, { TokenResource } from "../../../../../utilities/AuthModule";
import { IAuthTokenResultDto } from "../../../interfaces/IAuthTokenResultDto";
import generalModule from "../../../../../utilities/GeneralModule";
import { useLazyGetTasksQuery } from "../../_v2/redux/api/nintex/api-nintex-slice";
import { NintexTaskDisplay } from "../../_v2/redux/api/nintex/api-nintex-interface";
import { logger } from "../../../../../utilities/logger";
import { shared } from "../../../config/constants";
import { ApprovalsApp24Regular } from "../../_v2/components/SvgIcons/ApprovalsApp24Regular";

const refreshIcon: IIconProps = {
    iconName: "refresh",
    styles: {
        root: { color: "#323130" }
    }
};

const textStyle: ILabelStyles = {
    root: {
        fontSize: FontSizes.size12,
        fontWeight: FontWeights.regular,
        margin: 0,
        padding: 0
    }
}

const mapStateToProps = (state: RootState) => {
    return {
        isApprovalsLoading: state.approvals.isApprovalsLoading,
        approvals: state.approvals.approvals,
        approvalCount: state.approvals.approvalCount,
        lastApprovalLoad: state.approvals.lastApprovalLoad,
        expanders: state.expanders,
        tenantId: state.user.account.tenantId,
        nintexClientId: state.tenantInfo.nintexClientId,
        nintexClientSecret: state.tenantInfo.nintexClientSecret,
        username: state.user.account.username,
        isLoadedFromCache: state.user.isLoadedFromCache
    };
};

const mapDispatchToProps = {
    getApprovals,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

interface ApprovalsProps extends PropsFromRedux {
    requestFlowResourceToken?: (loginCallback: (authResult: IAuthTokenResultDto) => Promise<void>) => Promise<void>;
}

const MyApprovals: React.FunctionComponent<ApprovalsProps> = ({
    isApprovalsLoading,
    approvals,
    approvalCount,
    lastApprovalLoad,
    expanders,
    tenantId,
    getApprovals,
    requestFlowResourceToken,
    nintexClientId,
    nintexClientSecret,
    username,
    isLoadedFromCache
}: ApprovalsProps) => {
    const languageStrings = React.useContext(LanguageContext);
    const [isApprovalsPanelOpen, setIsApprovalsPanelOpen] = React.useState(false);
    const [selectedApproval, setSelectedApproval] = React.useState<IApproval>(null);
    const [actionApprovalList, setApprovalActionList] = React.useState<IApprovalAction[]>([]);
    const [selectedAction, setSelectedAction] = React.useState<IApprovalAction>(null);
    const [isApprovalsEnabled, setIsApprovalsEnabled] = React.useState<boolean>(authModule.getPersistedAuthState()?.flowToken?.length > 0);
    const [isApprovalsRefreshing, setIsApprovalsRefreshing] = React.useState<boolean>(false);
    const [nintexTasks, setNintexTasks] = React.useState<NintexTaskDisplay[]>([]);
    const [isNintex, setIsNintex] = React.useState(false);

    const [getTasks] = useLazyGetTasksQuery();

    React.useEffect(() => {
        if (!isApprovalsEnabled) {
            (async () => {
                setIsApprovalsRefreshing(true);
                try {
                    const flowToken: string = await authModule.getFlowAccessToken(true);
                    if (flowToken) {
                        setIsApprovalsEnabled(true);
                        refresh();
                    }
                } catch (error) {
                    setIsApprovalsRefreshing(false);
                }
            })();
        }
    }, [isApprovalsEnabled]);

    React.useEffect(() => {
        if (lastApprovalLoad) {
            getNintexTasks();
        }
    }, [lastApprovalLoad]);

    React.useEffect(() => {
        (async () => {
            if (nintexClientId && nintexClientSecret) {
                setIsNintex(true);
                await getNintexTasks();
            }
        })()

    }, [nintexClientId, nintexClientSecret]);

    React.useEffect(() => {
        if (selectedApproval) {
            setIsApprovalsPanelOpen(true);
        }
    }, [selectedApproval]);

    const getNintexTasks = async () => {
        try {
            setIsApprovalsRefreshing(true);

            const taskListResult = await getTasks({ assignee: username, accessToken: await authModule.getNintexAccessToken(nintexClientId, nintexClientSecret) }).unwrap();

            const nintexTasksDisplay: NintexTaskDisplay[] = [];
            taskListResult.tasks.forEach(task => {
                task.taskAssignments.filter(x => x.assignee === username).forEach(assignment => {
                    nintexTasksDisplay.push({
                        createdDate: assignment.createdDate,
                        urls: assignment.urls,
                        initiator: task.initiator,
                        name: task.name,
                        description: task.description,
                    })
                });
            });

            setNintexTasks(nintexTasksDisplay);

            setIsApprovalsRefreshing(false);
        } catch (error) {
            logger(error, "MyApprovals.tsx (getNintexTasks)");
            setIsApprovalsRefreshing(false);
        }
    }

    const loginCallback = async (authResult: IAuthTokenResultDto) => {
        if (authResult) {
            setIsApprovalsEnabled(authResult?.flowToken?.length > 0);
            getApprovals(tenantId);
        }
    };

    const requestFlowScope = async () => {
        if (generalModule.isInConentMangerMode()) {
            let authUrl: URL = new URL(process.env.baseUri + "/auth.html");
            authUrl.searchParams.append(shared.params.app, "content_manager");
            authUrl.searchParams.append(shared.params.resource, TokenResource.Flow.toString());
            authUrl.searchParams.append(shared.params.username, authModule.getPersistedAuthState()?.account?.username);
            window.location.replace(authUrl.href);
        }
        else {
            await authModule.initiateDialogAuthFlow(loginCallback, TokenResource.Flow);
        }
    }

    const handleItemClick = (approvalItem: IApproval) => {
        setSelectedApproval(approvalItem);
        const actionList: IApprovalAction[] = approvalItem.properties.userRequest.responseOptions.map((option, index) => {
            return ({
                name: option,
                id: index,
            })
        })
        let totalList: IApprovalAction[] = actionList;
        if (approvalItem.properties.userRequest.allowReassignment) {
            let localizedReassign = defaultApprovalActions.find(action => action.id === DefaultAction.REASSIGN);
            localizedReassign.name = languageStrings.Reassign;
            totalList = totalList.concat(localizedReassign);
        }
        setApprovalActionList(totalList);
    }

    const refresh = React.useCallback(() => {
        setIsApprovalsRefreshing(true);
        setApprovalActionList([]);
        setSelectedAction(null);
        setSelectedApproval(null);
        getApprovals(tenantId, false, refreshCompleted);
    }, []);

    const refreshNintexTasks = React.useCallback(async () => {
        await getNintexTasks();
    }, [nintexTasks]);

    const refreshCompleted = React.useCallback(() => {
        setIsApprovalsRefreshing(false);
    }, []);

    const handleNintexTaskClick = (task: NintexTaskDisplay) => {
        const winLeft = window.screenLeft ? window.screenLeft : window.screenX;
        const winTop = window.screenTop ? window.screenTop : window.screenY;

        const width = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;
        const height = window.innerHeight || document.documentElement.clientHeight || document.body.clientHeight;
        const left = Math.max(0, ((width / 2) - (600 / 2)) + winLeft);
        const top = Math.max(0, ((height / 2) - (800 / 2)) + winTop);

        const nintexWin = window.open(task.urls.formUrl, "Docubird.Nintex", `width=615, height=500, top=${top}, left=${left}, scrollbars=yes`);

        const intervalId = setInterval(() => {
            if (nintexWin.closed) {
                clearInterval(intervalId);
                getNintexTasks();
            }
        }, 1000);
    }

    const isApprovalSectionExpanded = React.useMemo(() => !expanders[expanderConstants.home.approvalCenter], [expanders[expanderConstants.home.approvalCenter]]);
    const displayApprovalCount = React.useMemo(() => {
        if (!isLoadedFromCache) {
            if (!isApprovalSectionExpanded && !isApprovalsLoading && !isApprovalsRefreshing) {
                return true;
            }
            else if (isApprovalSectionExpanded && (!isApprovalsLoading || isApprovalsRefreshing)) {
                return true;
            }
        }
        return false;
    }, [isLoadedFromCache, isApprovalSectionExpanded, isApprovalsLoading, isApprovalsRefreshing, isNintex]);

    const refreshItem = (isApprovalsEnabled || isNintex) &&
        <TooltipHost content={languageStrings.Refresh}>
            <ActionButton
                iconProps={refreshIcon}
                styles={{ root: { padding: 0, height: 25 } }}
                allowDisabledFocus
                onClick={(event) => {
                    event.stopPropagation();
                    isNintex ? refreshNintexTasks() : refresh();
                }}
            ></ActionButton>
        </TooltipHost>;

    let nintexItems = nintexTasks && nintexTasks.length > 0 &&
        nintexTasks.map((task, index) => {

            return (
                <div key={index} className="list-item" style={isMobile ? mobileListItemStyle : {}}>
                    <div className="list-item-link" onClick={() => {
                        handleNintexTaskClick(task)
                    }}>
                        <div className="list-item-icon">
                            <Persona
                                size={PersonaSize.size24}
                                text={task.initiator}
                                hidePersonaDetails
                            />
                        </div>
                        <Text className="list-item-name">
                            <TooltipHost content={<>
                                <Label>{task.name}</Label>
                                <Label styles={textStyle}>{task.description}</Label>
                            </>}>
                                {task.name}

                                <div className="xb-grey-text">{(new Date(task.createdDate)).toLocaleDateString() + " " + languageStrings.From.toLowerCase() + " " + task.initiator}</div>
                            </TooltipHost>
                        </Text>
                    </div>
                </div>
            )
        });


    let siteItems =
        isApprovalsEnabled && approvals && approvals.length > 0
            ? approvals.map((item: IApproval, index: number) => (
                <div key={index} className="list-item" style={isMobile ? mobileListItemStyle : {}}>
                    <div className="list-item-link" onClick={() => {
                        handleItemClick(item)
                    }}>
                        <div className="list-item-icon">
                            <Persona
                                size={PersonaSize.size24}
                                text={item.properties.principals[0].displayName}
                                hidePersonaDetails
                            />
                        </div>
                        <Text className="list-item-name">
                            <TooltipHost content={<>
                                <Label>{item.properties.title}</Label>
                                <Label styles={textStyle}>{item.properties.details}</Label>
                            </>}>
                                {item.properties.title}

                                <div className="xb-grey-text">{(new Date(item.properties.creationDate)).toLocaleDateString() + " " + languageStrings.From.toLowerCase() + " " + item.properties.principals[0].displayName}</div>
                            </TooltipHost>
                        </Text>
                    </div>
                    {
                        item.properties.type === ApprovalType.BASIC && <>
                            <TooltipHost content={languageStrings.Approve}>
                                <div className={isMobile ? "list-item-save-icon-mobile" : "list-item-save-icon"}>
                                    <FontIcon
                                        iconName="CheckMark"
                                        onClick={() => {
                                            setSelectedAction({
                                                name: item.properties.userRequest.responseOptions[0],
                                                id: 0
                                            })
                                            handleItemClick(item);
                                        }}
                                    />
                                </div>
                            </TooltipHost>
                            <TooltipHost content={languageStrings.Reject}>
                                <div className={isMobile ? "list-item-save-icon-mobile" : "list-item-save-icon"}>
                                    <FontIcon
                                        iconName="Cancel"
                                        onClick={() => {
                                            setSelectedAction({
                                                name: item.properties.userRequest.responseOptions[1],
                                                id: 1
                                            })
                                            handleItemClick(item);
                                        }}
                                    />
                                </div>
                            </TooltipHost>
                        </>
                    }
                </div>
            ))
            : !isApprovalsLoading
                ? isApprovalsEnabled
                    ? [
                        <div key="0">
                            <p className="xb-empty-location-message centerText" style={{ marginTop: 0 }}>
                                {languageStrings.ApprovalsShownHere}
                            </p>
                        </div>
                    ]
                    : [<TooltipHost key="requestLink" content={languageStrings.AllowApprovalsMessage}>
                        <Stack horizontal horizontalAlign="center" style={{ marginTop: 12 }}>
                            <DefaultButton
                                onClick={async () => {
                                    if (!requestFlowResourceToken) {
                                        await requestFlowScope();
                                    }
                                    else {
                                        await requestFlowResourceToken(loginCallback);
                                    }
                                }}
                            >
                                {languageStrings.AllowApprovals}
                            </DefaultButton>
                        </Stack>
                    </TooltipHost>]
                : [];

    const getExpanderTitle = () => {
        if (isNintex) {
            return `${languageStrings.ApprovalCenter} (${nintexItems.length || 0})`
        }

        return displayApprovalCount ? `${languageStrings.ApprovalCenter} (${approvalCount})` : languageStrings.ApprovalCenter;
    }

    return (
        <>
            <ExpanderWithSubtitle
                // iconName="DocumentApproval"
                iconElement={<ApprovalsApp24Regular />}
                items={isNintex ? nintexItems : siteItems}
                title={getExpanderTitle()}
                isWaitingOnContent={isNintex ? isApprovalsRefreshing : (isLoadedFromCache || isApprovalsLoading)}
                expanderNameToToggle={expanderConstants.home.approvalCenter}
                actionButton={refreshItem}
            />
            <ApprovalsPanel
                isApprovalsPanelOpen={isApprovalsPanelOpen}
                dismissApprovalsPanel={() => {
                    setIsApprovalsPanelOpen(false);
                    setSelectedApproval(null);
                }}
                approval={selectedApproval}
                passedAction={selectedAction}
                approvalActionList={actionApprovalList}
            />
        </>
    );
};

export default connector(MyApprovals);